import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Avatar,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import logoOnly from "../../assets/IndeeFlix-logo-only.png"; // Your fallback logo
import { getChannelByID, getSubscribers } from "../../services/APIServices/channel.api";
import { useAppSelector } from "../../store/hooks";
import { getUser } from "../../store/reducers/user.reducer";
import { useNavigate } from "react-router-dom";
import { PAGE_ROUTES, ROUTES_PATH } from "../../utils/constants";
import moment from "moment";
import "moment/locale/hi";
import { inscribeChannel } from "../../services/APIServices/user.api";
import { useAuth } from "../../context/AuthContext";
import { isUserChannel } from "../../utils";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";

const Subscriptions = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  let user = useAppSelector((state) => getUser(state));
  let navigate = useNavigate();
  const { t, i18n } = useTranslation();
  moment.locale(i18n.language);
  const { verifyAuth, setOnLoginSuccess } = useAuth();

  useEffect(() => {
    if (!user?.userID) {
      navigate(ROUTES_PATH.LOGIN, {
        state: {
          redirectTo: PAGE_ROUTES.SUBSCRIPTIONS_PAGE,
        },
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchSubscribersWithDetails = async () => {
      try {
        const response = (await getSubscribers()) as any;
        const subscribedList = response || [];

        const enrichedList = await Promise.all(
          subscribedList.map(async (item: any) => {
            let details = null;
            try {
              const res = await getChannelByID(item.channelID, user?.userID || "");
              const channelById = res as any;
              details = channelById.channelID as any ? channelById : null;
            } catch (err) {
              console.error(`Failed to fetch channel details for ${item.channelID}`, err);
            }
            return {
              ...item,
              details,
            };
          })
        );

        setChannels(enrichedList as any);
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        setChannels([]);
      } finally {
        setLoading(false);
      }
    };

    if (loading) {
      if (user?.userID) {
        fetchSubscribersWithDetails();
      }
    }
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  function truncateDescription(description: string, maxLength: number) {
    if (description) {
      if (description.length <= maxLength) {
        return description;
      }
      const words = description.split(" ");
      const truncatedWords = words.slice(0, maxLength);
      return truncatedWords.join(" ") + "...";
    }
    return "";
  }

  const handleInscribe = async (channelID: string, userID: any) => {
    // Optimistic UI update
    setChannels((prev: any) =>
      prev.map((channel: any) =>
        channel.channelID === channelID
          ? {
            ...channel,
            details: {
              ...channel.details,
              isSubscribed:
                channel.details?.isSubscribed === true ||
                  channel.details?.isSubscribed === "SUBSCRIBED"
                  ? false
                  : true,
            },
          }
          : channel
      )
    );

    try {
      const res = await inscribeChannel(channelID as string, userID);

      // 🟢 ADD THIS — if unsubscribed, remove from list immediately
      setChannels((prev) =>
        prev.filter(
          (channel: any) =>
            !(
              channel.channelID === channelID &&
              (channel.details?.isSubscribed === false ||
                channel.details?.isSubscribed === "UNSUBSCRIBED")
            )
        )
      );
    } catch (err) {
      console.error("Subscribe/unsubscribe failed:", err);
    }
  };

  const handleSubscribeClick = (channelID: any, userID: any) => {
    if (verifyAuth()) {
      handleInscribe(channelID, userID);
    } else {
      setOnLoginSuccess(handleInscribe);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        {t("subscribers.heading")}
      </Typography>

      {channels.length === 0 ? (
        <Typography>{t("subscribers.noSubscriptions")}</Typography>
      ) : (
        <Grid container spacing={2}>
          {channels.map((channel: any) => (
            <Grid item xs={12} sm={12} md={12} key={channel.channelID}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  p: 2,
                  gap: 2,
                  position: "relative", // 👈 for absolute positioning inside
                }}
              >
                <Avatar
                  src={channel.logoUrl || logoOnly}
                  alt={channel.name}
                  sx={{
                    width: isMobile ? 50 : 140,
                    height: isMobile ? 50 : 140,
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    navigate(
                      `${PAGE_ROUTES.CHANNEL_PAGE}/profile/${channel?.channelID}`
                    )
                  }
                />
                <Box sx={{ flex: 1, position: "relative", cursor: "pointer" }} onClick={() => navigate(`${PAGE_ROUTES.CHANNEL_PAGE}/profile/${channel?.channelID}`)}>
                  <Typography fontWeight="bold">{channel.name}</Typography>
                  <Typography
                    display="block"
                    variant="caption"
                    color="text.secondary"
                  >
                    {`${channel.details?.channelTag?.startsWith("@") ? channel.details.channelTag : `@${channel.details?.channelTag}`} • ${channel.details?.subscriberCount ?? "1.5M subscribers"}`}
                  </Typography>
                  <Typography
                    display="block"
                    variant="caption"
                    color="text.secondary"
                  >
                    {truncateDescription(
                      channel.details?.channelDescription ?? "",
                      8
                    ) || t("subscribers.noDescription")}
                  </Typography>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {`${t("subscribers.published")} • ${moment(
                        `${channel.details?.creationTime
                          ?.split("T")
                          ?.join(" ")} UTC`
                      ).fromNow()}`}
                    </Typography>
                  </Stack>
                </Box>

                <Chip
                  size={isMobile ? "small" : "medium"}
                  color="secondary"
                  label={
                    (typeof channel.details?.isSubscribed === "boolean" &&
                      typeof channel.details?.isSubscribed) ||
                      channel.details?.isSubscribed === "SUBSCRIBED"
                      ? t("subscribers.unsubscribe")
                      : t("subscribers.subscribe")
                  }
                  sx={{
                    cursor: "pointer",
                    position: "absolute",
                    top: isMobile ? 43 : 53,
                    right: isMobile ? 15 : 30,
                  }}
                  onClick={() => handleSubscribeClick(channel.channelID, user?.userID)}
                  disabled={isUserChannel(user?.channelList, channel.channelID)}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Subscriptions;